import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, UserIcon, CalendarDaysIcon, MapPinIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import adminAPI from '../utils/api';

const CreateBookingModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Select Client, 2: Select Service, 3: Select Session
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientServices, setClientServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');

  // Fetch initial data
  useEffect(() => {
    if (isOpen) {
      fetchClients();
      // Set default date to today
      const today = new Date().toISOString().split('T')[0];
      setSelectedDate(today);
    }
  }, [isOpen]);

  // Fetch sessions when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchSessions();
    }
  }, [selectedDate]);

  const fetchClients = async () => {
    try {
      const response = await adminAPI.getUsers({ role: 'client' });
      setClients(Array.isArray(response) ? response : (response.results || []));
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Erreur lors du chargement des clients');
    }
  };

  const fetchSessions = async () => {
    if (!selectedDate) return;

    try {
      setLoadingSessions(true);
      const response = await adminAPI.getSessions({
        date: selectedDate, // Filter by specific date
        status: 'available'
      });
      setSessions(Array.isArray(response) ? response : (response.results || []));
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Erreur lors du chargement des séances');
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchClientServices = async (clientId) => {
    try {
      setLoadingServices(true);
      const response = await adminAPI.getClientServices({ client_id: clientId });
      const services = Array.isArray(response) ? response : (response.results || []);
      
      // Filter only active services that can be used for booking
      const activeServices = services.filter(service => {
        const today = new Date();
        const endDate = new Date(service.end_date);
        const isActive = service.status === 'active';
        const notExpired = endDate >= today;
        const hasRemainingSessions = service.service_type?.type === 'pack' 
          ? service.used_sessions < service.total_sessions
          : service.current_month_used < (service.current_month_sessions || service.service_type?.session_count || 0);
        
        return isActive && notExpired && hasRemainingSessions;
      });
      
      setClientServices(activeServices);
    } catch (error) {
      console.error('Error fetching client services:', error);
      toast.error('Erreur lors du chargement des services du client');
    } finally {
      setLoadingServices(false);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSelectedService(null);
    setSelectedSession(null);
    fetchClientServices(client.id);
    setStep(2);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedSession(null);
    setStep(3);
  };

  const handleSessionSelect = (session) => {
    setSelectedSession(session);
  };

  const handleSubmit = async () => {
    if (!selectedClient || !selectedService || !selectedSession) {
      toast.error('Veuillez sélectionner un client, un service et une séance');
      return;
    }

    setLoading(true);
    try {
      await adminAPI.createBooking({
        client_id: selectedClient.id,
        session_id: selectedSession.id,
        client_service_id: selectedService.id
      });
      
      toast.success('Réservation créée avec succès');
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.message || 'Erreur lors de la création de la réservation');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSession(null); // Reset selected session when date changes
  };

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    if (!clientSearchTerm.trim()) return clients;

    const searchLower = clientSearchTerm.toLowerCase();
    return clients.filter(client =>
      client.full_name?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.first_name?.toLowerCase().includes(searchLower) ||
      client.last_name?.toLowerCase().includes(searchLower)
    );
  }, [clients, clientSearchTerm]);

  const handleClose = () => {
    setStep(1);
    setSelectedClient(null);
    setSelectedService(null);
    setSelectedSession(null);
    setClientServices([]);
    setSelectedDate('');
    setClientSearchTerm('');
    onClose();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Créer une nouvelle réservation
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${step >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                1
              </div>
              <span className="ml-2 text-sm font-medium">Client</span>
            </div>
            <div className="flex-1 h-px bg-gray-200"></div>
            <div className={`flex items-center ${step >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm font-medium">Service</span>
            </div>
            <div className="flex-1 h-px bg-gray-200"></div>
            <div className={`flex items-center ${step >= 3 ? 'text-primary-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 3 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                3
              </div>
              <span className="ml-2 text-sm font-medium">Séance</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Select Client */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Sélectionner un client</h3>

              {/* Client Search */}
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher un client par nom ou email..."
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => handleClientSelect(client)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center">
                      <UserIcon className="h-8 w-8 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {client.full_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {client.email}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredClients.length === 0 && clientSearchTerm && (
                <div className="text-center py-8 text-gray-500">
                  <UserIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun client trouvé pour "{clientSearchTerm}"</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Service */}
          {step === 2 && selectedClient && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">Services actifs pour {selectedClient.full_name}</h3>
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  ← Changer de client
                </button>
              </div>
              
              {loadingServices ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Chargement des services...</p>
                </div>
              ) : clientServices.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Aucun service actif trouvé pour ce client</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {clientServices.map((service) => (
                    <div
                      key={service.id}
                      onClick={() => handleServiceSelect(service)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {service.service_type?.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {service.service_type?.type === 'pack' ? 'Pack' : 'Abonnement'}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Période: {service.start_date} - {service.end_date}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {service.service_type?.type === 'pack' 
                              ? `${service.total_sessions - service.used_sessions} séances restantes`
                              : `${(service.current_month_sessions || service.service_type?.session_count || 0) - service.current_month_used} séances ce mois`
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select Session */}
          {step === 3 && selectedService && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">Sélectionner une séance</h3>
                <div className="text-sm text-gray-600 mb-2">
                  Service: {selectedService.service_type?.name} pour {selectedClient.full_name}
                </div>
                <button
                  onClick={() => setStep(2)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  ← Changer de service
                </button>
              </div>

              {/* Date Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner une date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  min={new Date().toISOString().split('T')[0]} // Minimum today
                  className="w-full md:w-auto p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              {!selectedDate ? (
                <div className="text-center py-8">
                  <CalendarDaysIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Veuillez sélectionner une date pour voir les séances disponibles</p>
                </div>
              ) : loadingSessions ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Chargement des séances...</p>
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarDaysIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune séance disponible pour le {new Date(selectedDate).toLocaleDateString('fr-FR')}</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => handleSessionSelect(session)}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedSession?.id === session.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {session.title || 'Séance'}
                          </div>
                          <div className="text-sm text-gray-600 flex items-center mt-1">
                            <CalendarDaysIcon className="h-4 w-4 mr-1" />
                            {new Date(session.date).toLocaleDateString('fr-FR')} à {session.start_time}
                          </div>
                          <div className="text-sm text-gray-600 flex items-center mt-1">
                            <MapPinIcon className="h-4 w-4 mr-1" />
                            {session.location?.name}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Coach: {session.coach?.user?.full_name}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">
                            {session.current_bookings}/{session.max_capacity} places
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Annuler
          </button>
          {step === 3 && selectedSession && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Création...' : 'Créer la réservation'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateBookingModal;
